import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { GPSSource } from "PosterEditor/model/poster-project";
import { v4 as uuid } from "uuid";

export const useGpsStoreRemote = () => {
  const { userID, client: supabase } = useAuthContext();

  const saveCoordinates = async (
    coordinates: GPSSource,
    id: string,
    isPublic: boolean
  ) => {
    if (!userID)
      throw new Error("useGPSRemoteStore 13: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");
    const filePath = isPublic
      ? `gps-data/${id}.json`
      : `user-data/${userID}/${id}.json`;

    const file = new Blob([JSON.stringify(coordinates)], {
      type: "application/json",
    });

    const { error } = await supabase.storage
      .from(isPublic ? "public-gps-data" : "gps-data")
      .upload(filePath, file, {
        upsert: true,
      });

    if (error) throw error;
  };

  const fetchCoordinates = async (
    id: string,
    isPublic: boolean
  ): Promise<GPSSource | null> => {
    if (!isPublic && !userID)
      throw new Error("useGPSRemoteStore 37: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");

    const filePath = isPublic
      ? `gps-data/${id}.json`
      : `user-data/${userID}/${id}.json`;

    const { data, error } = await supabase.storage
      .from(isPublic ? "public-gps-data" : "gps-data")
      .download(filePath);

    if (!data) {
      return null;
    }

    const text = await data.text();
    return JSON.parse(text) as GPSSource;
  };

  const copyGPSSourceFromPublicToUser = async (id: string) => {
    if (!userID)
      throw new Error("useGPSRemoteStore 57: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");

    //DO NOT CHANGE THESE PATHS.  THEY ARE CORRECT.
    const filePathInPublicGPSDataBucket = `/gps-data/${id}.json`;
    const newFilePathGPSDaraBucket = `user-data/${userID}/${id}.json`;

    //ONLY CHANGE THIS TO COPY FROM THE PUBLIC BUCKET TO THE USER BUCKET
    const { data, error: downloadError } = await supabase.storage
      .from("public-gps-data")
      .download(filePathInPublicGPSDataBucket);

    if (downloadError) throw downloadError;

    const { error: uploadError } = await supabase.storage
      .from("gps-data")
      .upload(newFilePathGPSDaraBucket, data, { upsert: true });

    if (uploadError) throw uploadError;
  };

  const copyPublicGPSSource = async (id: string): Promise<string> => {
    if (!userID)
      throw new Error("useGPSRemoteStore 80: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");

    const sourceFilePath = `gps-data/${id}.json`;
    const newID = uuid();
    const destinationFilePath = `gps-data/${newID}.json`;

    const { data, error: downloadError } = await supabase.storage
      .from("public-gps-data")
      .download(sourceFilePath);

    if (downloadError) throw downloadError;

    const { error: uploadError } = await supabase.storage
      .from("public-gps-data")
      .upload(destinationFilePath, data, { upsert: true });

    if (uploadError) throw uploadError;

    return newID;
  };

  const deleteCoordinates = async (id: string, isPublic: boolean) => {
    if (!userID)
      throw new Error("useGPSRemoteStore 103: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");

    const filePath = `user-data/${userID}/${id}.json`;

    const { error } = await supabase.storage
      .from("gps-data")
      .remove([filePath]);

    if (error) throw error;
  };

  const getImageUrl = async (
    id: string,
    isPublic: boolean
  ): Promise<string | null> => {
    if (!supabase) throw new Error("Supabase client not initialized");

    const filePath = isPublic ? `${id}.png` : `${userID}/${id}.png`;

    if (isPublic) {
      const { data } = supabase.storage
        .from("public-images")
        .getPublicUrl(filePath);
      return data?.publicUrl || null;
    }

    if (!userID)
      throw new Error("useGPSRemoteStore 130: User not authenticated");

    const { data, error } = await supabase.storage
      .from("user-images")
      .createSignedUrl(filePath, 60 * 60); // 1 hour

    if (error || !data) return null;
    return data.signedUrl;
  };

  return {
    saveCoordinates,
    fetchCoordinates,
    copyGPSSourceFromPublicToUser,
    deleteCoordinates,
    getImageUrl,
    copyPublicGPSSource,
  };
};
